import { useEffect, useEffectEvent, useRef, useState } from "react";
import { TM } from "@litres-libs/tracker-manager";
import clsx from "clsx";

import { getConfig } from "lib/get-config";
import { useTranslation } from "lib/i18n/use-translation";
import { Overlay } from "lib/ui/overlay";

import CloseIcon from "assets/icons/close24.svg?react";
import css from "./saleblock.module.scss";

const IFRAME_CLOSE = "IFRAME_SALE_BLOCK_CLOSE" as const;
const IFRAME_RESIZE = "IFRAME_SALE_BLOCK_RESIZE" as const;
const IFRAME_MESSAGE_SOURCE = "IFRAME_SALE_BLOCK" as const;

type SaleblockCloseMessage = {
  source: typeof IFRAME_MESSAGE_SOURCE;
  type: typeof IFRAME_CLOSE;
};

type SaleblockResizeMessage = {
  source: typeof IFRAME_MESSAGE_SOURCE;
  type: typeof IFRAME_RESIZE;
  height: number;
};

type SaleblockMessage = SaleblockCloseMessage | SaleblockResizeMessage;

/**
 * Функция проверки что postMessage от нашего iframe
 */
function isSaleblockMessage(data: unknown): data is SaleblockMessage {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;

  return (
    d.source === IFRAME_MESSAGE_SOURCE &&
    (d.type === IFRAME_CLOSE || d.type === IFRAME_RESIZE)
  );
}

export function Saleblock() {
  const { art, showSaleBlock, isAvailableBySubscription, uilang } =
    getConfig() as ReturnType<typeof getConfig> & Record<string, unknown>;
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [iframeHeight, setIframeHeight] = useState<number | undefined>(
    undefined
  );
  const isCloseEventSentRef = useRef(false);

  const src = getIframeUrl(art, uilang);

  function handleClose() {
    if (isCloseEventSentRef.current) return;
    isCloseEventSentRef.current = true;
    TM.sendEventSync({
      eventType: "saleblock_closed",
      params: {
        id: art,
        book_card_main_offer: "PPD",
        book_card_secondary: isAvailableBySubscription
          ? "buy_subscription"
          : "",
        source_internal: "book_card",
      },
    });

    setIsOpen(false);
  }

  const handleMessage = useEffectEvent((event: MessageEvent) => {
    if (!isSaleblockMessage(event.data)) return;

    switch (event.data.type) {
      case IFRAME_CLOSE:
        setIsOpen(false);
        break;
      case IFRAME_RESIZE:
        setIframeHeight(prev => {
          if (prev === undefined) {
            isCloseEventSentRef.current = false;
            setIsOpen(true);
          }
          return event.data.height;
        });
        break;
    }
  });

  useEffect(() => {
    if (!showSaleBlock) return;

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  if (!showSaleBlock) return null;

  return (
    <>
      <Overlay open={isOpen} onClick={handleClose} className={css.overlay} />
      <div className={clsx(css.root, isOpen && css.open)} inert={!isOpen}>
        <button
          type="button"
          className={css.closeButton}
          onClick={handleClose}
          aria-label={t("Закрыть")}
        >
          <CloseIcon className={css.closeIcon} />
        </button>
        <iframe
          src={src}
          className={css.iframe}
          style={
            iframeHeight !== undefined ? { height: iframeHeight } : undefined
          }
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation-by-user-activation"
          referrerPolicy="no-referrer-when-downgrade"
          title="Saleblock"
          scrolling="no"
        />
      </div>
    </>
  );
}

/**
 * Функция получения url iframe
 * Выставляем язык в зависимости от uilang
 * Параметр debug_iframe_url для отладки и указания кастомного url iframe
 * */
function getIframeUrl(art: number, uilang: string): string {
  const clientHost = getDomainByLangCode(uilang);
  const searchParams = new URLSearchParams(window.location.search);
  const debugIframeUrl = searchParams.get("debug_iframe_url");
  const baseUrl = debugIframeUrl || `litres.ru`;

  return `https://${baseUrl}/iframe/saleblock/${art}/?domain=${clientHost}`;
}

function getDomainByLangCode(clientHost: string): string {
  switch (clientHost) {
    case "en":
      return "litres.com";
    case "az":
      return "litres.az";
    case "uz":
      return "litres.uz";
    case "tr":
      return "litres-tr.com";
    case "pl":
      return "litres.pl";
    case "de":
      return "litres.de";
    default:
      return "litres.ru";
  }
}
